import { Orbit, Pan, Fly, Web3DViewer } from '@technology/web3d';
import { Web3dTool, CapabilityProviderBase } from '@trimble/web3d-providers';
import { createSignal, createEffect, Accessor } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { NavigationalCapabilityBase } from './base/navigational-capability-base';
import { getLogger } from '@trimble/logging';
import { NavigationTool } from '../helpers/web3d-tool-helper';

export enum FreeViewMode {
  Rotate = 'rotate',
  Pan = 'pan',
  LookAround = 'lookAround',
}

// This meant to be the default extension when nothing else is activated.
export class FreeViewCapability extends NavigationalCapabilityBase {
  protected override requiredWeb3DTools: NavigationTool[] = [
    Web3dTool.Fly,
    Web3dTool.Orbit,
    Web3dTool.Pan,
    Web3dTool.Zoom,
    Web3dTool.CameraShortcuts,
  ];

  private readonly _mode!: Accessor<FreeViewMode>;
  private readonly _setMode!: (mode: FreeViewMode) => void;
  private readonly mouseButton = { left: 0, middle: 1, right: 2 } as const;

  private readonly orbitTool: Orbit;
  private readonly panTool: Pan;
  private readonly flyTool: Fly;
  protected logger = getLogger();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(viewer: Web3DViewer, provider?: CapabilityProviderBase<any>) {
    super(viewer, provider);

    this.orbitTool = this.viewer.tools.get(Orbit)!;
    this.panTool = this.viewer.tools.get(Pan)!;
    this.flyTool = this.viewer.tools.get(Fly)!;

    const [mode, setMode] = createSignal<FreeViewMode>(FreeViewMode.Rotate);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any)._mode = mode;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any)._setMode = setMode;

    createEffect(() => {
      const mode = this._mode();

      switch (mode) {
        case FreeViewMode.Rotate:
          this.orbitTool.mouseButton = this.mouseButton.left;
          this.panTool.mouseButton = this.mouseButton.middle;
          this.flyTool.mouseButton = this.mouseButton.right;
          break;
        case FreeViewMode.Pan:
          this.panTool.mouseButton = this.mouseButton.left;
          this.orbitTool.mouseButton = this.mouseButton.middle;
          this.flyTool.mouseButton = this.mouseButton.right;
          break;
        case FreeViewMode.LookAround:
          this.flyTool.mouseButton = this.mouseButton.left;
          this.panTool.mouseButton = this.mouseButton.middle;
          this.orbitTool.mouseButton = this.mouseButton.right;
          break;
        default:
          throw new UnreachableCaseError(mode);
      }
    });
  }

  public get mode(): Accessor<FreeViewMode> {
    return this._mode;
  }

  public setMode(mode: FreeViewMode): void {
    this._setMode(mode);
  }

  public override cleanup(): Promise<void> {
    return Promise.resolve();
  }
}
